import React, { useState, useCallback } from 'react';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';

import RichEditorContent from 'components/RichEditorContent';

function CmsElementText(props) {
  const { index, showElementSettings, editMode } = props;

  // --- Local State ---
  const [shown, setShown] = useState(false);

  // --- Abstract Logic Hook ---
  const {
    getMainId,
    getCssClasses,
    getElementStyle,
    getBackgroundStyle,
    getParamValue,
    getParallaxData,
    getCustomId,
    getAppendHtml,
  } = useAbstractCmsElement(props);

  // --- dnd-kit Setup ---
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: index });

  const dndStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 1000 : 1,
  };

  // --- Handlers ---
  const handleShown = useCallback(() => setShown(true), []);

  const handleShowSettings = (e) => {
    if (showElementSettings) {
      showElementSettings(e, index);
    }
  };

  // --- UI Variables ---
  const shownClass = shown ? 'elem-shown ' : 'elem-hidden ';
  const content = getParamValue('content') || '';
  const mainColor = getParamValue('mainColor', 'master');
  const parallaxData = getParallaxData();

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-text relative ${shownClass}${getCssClasses()}`}
      style={{
        ...getBackgroundStyle(),
        /*
           Disable pointer events on the content during edit mode
           to ensure dnd-kit catches the drag intent on the wrapper.
        */
        pointerEvents: editMode ? 'none' : 'auto',
        userSelect: editMode ? 'none' : 'auto',
      }}
    >
      {getElementStyle()}

      <RichEditorContent content={`<div class="text-${mainColor}">${content}</div>`} />

      {!content && (
        <div className="p-t-15 p-b-15 text-center">
          <i className="far fa-file-alt fa-4x" />
        </div>
      )}

      {getAppendHtml()}
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      {...attributes}
      {...listeners}
      /*
         In editMode, the click is handled here because the inner content
         is "invisible" to pointer events. dnd-kit prevents this click
         if the user actually performs a drag move.
      */
      onClick={editMode ? handleShowSettings : undefined}
    >
      <CustomScrollTrigger className="cms-overflow-fix" onEnter={handleShown} {...getCustomId()}>
        {parallaxData ? <Parallax {...parallaxData}>{innerContent}</Parallax> : innerContent}
      </CustomScrollTrigger>

      {/* Visual drag indicator overlay for better UX in editor */}
      {editMode && (
        <div
          className="drag-handle-indicator"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: isDragging ? '2px solid #007bff' : '1px dashed transparent',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

export default CmsElementText;
